// The same patient's other incidents on this event, on the incident dialog: one
// collapsed line each, expandable in place to the details worth reading before
// treating them again. Fed by GET /incidents/patient-history/{event_id} (see
// pages/Incidents.jsx), which identifies the patient by their entry-list link or
// their race number.
//
// Reading it inline rather than in another tab is the point: the reader is part way
// through writing an incident, and sending them to a second copy of this page to
// find out what happened last time costs them their place. The open button stays
// for when they actually need the record — to add a note to a visit that is already
// recorded (rather than raising a second incident for it), to edit it, or to use
// anything this summary leaves out — and `onOpen` makes even that keep their place,
// swapping the incident into the dialog they're already in.
//
// **No permission logic lives here.** The server has already stripped the clinical
// fields and the drug notes from a reader without `Incidents.viewClinical` (see
// core/clinical.py), and every field below is dropped when it's empty — so a Race
// Organiser sees the same component render the non-clinical half of the record,
// with nothing to say it was ever more than that.
import { useState } from "react";
import {
  Box, Collapse, Divider, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { RichTextView } from "./RichText";
import { drugLine, standingDrugs, summaryNotes } from "../incidentNotes";
import { detailSubtypeLabel } from "../constants";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// "label: value" on one line, for the short fields. Dropped entirely when there's
// no value — an incident's summary of itself shouldn't be a list of blanks.
const Line = ({ label, value }) => (!value ? null : (
  <Typography variant="body2">
    <Box component="span" sx={{ color: "text.secondary" }}>{label}: </Box>{value}
  </Typography>
));

// A field whose text runs to several lines, so it gets its own caption and keeps
// the line breaks it was typed with.
const Block = ({ label, children }) => (
  <Box sx={{ mt: 0.75 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      {label}
    </Typography>
    {children}
  </Box>
);

// A person as the rest of the app names them on an incident: their name and, in
// brackets, their attendance role on the event.
const withRole = (name, role) => (name ? `${name}${role ? ` (${role}) ` : ""}`.trim() : "");

// One incident's key details, in the order the dialog itself shows them. What's
// deliberately absent: the event and the patient (both are the reason this row is
// here at all), the other four coordinate systems, the on-course questions, the
// consents, the status and the clinical review — and every control, since this is a
// record to read, not one to edit. The notes are pruned too, see summaryNotes.
function Details({ incident: i }) {
  const medical = [i.detail_type, detailSubtypeLabel(i)].filter(Boolean).join("  ·  ");
  const location = [i.location_type, i.location_details].filter(Boolean).join(" — ");
  // Who raised it and who holds it — the people to ask about this visit. An
  // allocation to a team is labelled as one, as it is in the incidents list; an
  // incident with no allocation recorded still sits with whoever raised it, which is
  // what the incident dialog says too, so that reads as the same person rather than
  // as nobody.
  const raisedBy = withRole(i.created_by_name, i.created_by_role);
  const allocatedTo = i.allocated_to_team_id != null
    ? `${i.allocated_to_name} (team)`
    : withRole(i.allocated_to_name, i.allocated_to_role);
  const sameHolder = !allocatedTo
    || (i.allocated_to_team_id == null && i.allocated_to_name === i.created_by_name);
  const netting = (i.safety_netting || []).join(", ");
  // Oldest first, with the app's own bookkeeping taken out: an allocation or a
  // share is about who was asked to look at the incident, which isn't why this
  // record is being read.
  const notes = summaryNotes(i.notes);
  // Drug and dose only — which batch it came out of is on the incident itself.
  // Served empty to a reader without the clinical gate, like the fields above.
  const drugs = standingDrugs(i.drug_administrations)
    .map(d => [drugLine(d), d.notes].filter(Boolean).join(" — "));
  return (
    <Box sx={{ pl: 4, pb: 1 }}>
      <Line label="Incident type" value={i.incident_type} />
      <Line label="Summary" value={i.incident_summary} />
      {/* One line when the person who raised it still holds it, which is the common
          case — two lines saying the same name reads as two people. */}
      {sameHolder ? (
        <Line label="Created by / allocated to" value={raisedBy} />
      ) : (
        <>
          <Line label="Created by" value={raisedBy} />
          <Line label="Allocated to" value={allocatedTo} />
        </>
      )}
      <Line label="Location" value={location} />
      {(i.incident_details || "").trim() && (
        <Block label="Incident details">
          <RichTextView value={i.incident_details} dense />
        </Block>
      )}
      <Line label="Medical details" value={medical} />
      {(i.treatment_given || "").trim() && (
        <Block label="Treatment given">
          {/* pre-wrap so a dose written over several lines survives, as in the
              incident's own Medical details section */}
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {i.treatment_given}
          </Typography>
        </Block>
      )}
      <Line label="Safety netting" value={netting} />
      {/* Only a recorded outcome is stated: blank is either "not advised" or "not
          shown to you" (a redacted read), and the two can't be told apart here. */}
      {i.advised_to_retire && (
        <Line label="Advised to retire" value={i.advised_to_retire} />
      )}
      {drugs.length > 0 && <Line label="Drugs given" value={drugs.join("; ")} />}
      {notes.length > 0 && (
        <Block label={`Notes (${notes.length})`}>
          <Stack spacing={0.5}>
            {notes.map(n => (
              <Box key={n.id} sx={{ border: "1px solid", borderColor: "divider",
                                    borderRadius: 1, p: 1 }}>
                {(n.details || "").trim()
                  ? <RichTextView value={n.details} dense />
                  : <Typography variant="body2">—</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {n.added_by || "—"}{n.added_by_role ? ` (${n.added_by_role})` : ""}
                  {" — "}{fmtDateTime(n.added_at)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Block>
      )}
    </Box>
  );
}

export default function PatientIncidentHistory({ incidents, onOpen }) {
  // Expanded rows, by id — a set rather than one at a time, so two visits can be
  // read side by side.
  const [expanded, setExpanded] = useState(() => new Set());
  if (!incidents || incidents.length === 0) return null;

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  return (
    <Box sx={{ border: "1px solid", borderColor: "warning.light", borderRadius: 1, p: 1.5 }}>
      <Typography variant="subtitle2" color="warning.main" gutterBottom>
        {incidents.length} other incident{incidents.length === 1 ? "" : "s"} for this patient
      </Typography>
      <Stack divider={<Divider />}>
        {incidents.map(i => {
          const isOpen = expanded.has(i.id);
          const headline = `${fmtDateTime(i.created_at)} — ${i.incident_type || "Incident"}`
            + `: ${i.incident_summary || "(no summary)"}`;
          return (
            <Box key={i.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* The whole line is the toggle, so it's reachable on a phone
                    without aiming at the chevron. */}
                <Box component="button" type="button" onClick={() => toggle(i.id)}
                     aria-expanded={isOpen}
                     sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.5,
                           py: 0.75, px: 0, border: 0, background: "none", cursor: "pointer",
                           textAlign: "left", font: "inherit", color: "inherit" }}>
                  <ExpandMoreIcon
                    sx={{ fontSize: 18, flexShrink: 0, transition: "transform 150ms",
                          transform: isOpen ? "rotate(180deg)" : "none" }} />
                  <Typography variant="body2" component="span">{headline}</Typography>
                </Box>
                {/* Opens the incident itself, for anything this summary leaves out
                    and so the reader can add a note to the visit that already
                    exists rather than raising a second incident for it. Offered on
                    every row: whoever may be told about the history may open it
                    (see authz.can_view_incident).
                    `onOpen` swaps it into the dialog in place and holds whatever
                    was being written to come back to — far better than a second
                    tab on a phone. The href is kept so ctrl/cmd-click still opens
                    a tab for anyone who wants the two side by side; only the plain
                    click is intercepted. */}
                <Tooltip title="Open this incident">
                  <IconButton size="small" href={`/incidents?open=${i.id}`}
                              rel="noopener"
                              onClick={(e) => {
                                if (!onOpen || e.metaKey || e.ctrlKey || e.shiftKey) return;
                                e.preventDefault();
                                onOpen(i.id);
                              }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Collapse in={isOpen} unmountOnExit>
                <Details incident={i} />
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
